import React, { useState, useRef, useContext } from "react";
import { any, arrayOf, bool, func, node, shape, string } from "prop-types";
import { useCombobox } from "downshift";

import { AppContext } from "../FloorPlansPage/AppContext.jsx";

import cx from "classnames";
import * as R from "ramda";
import useClickAway from "../../common/useClickAway.js";

const propTypes = {
  className: string,
  disabled: bool,
  itemToId: func, // can use this instead of itemToString to identify an item
  label: string,
  placeholder: node,
  renderItem: func, // (item, isSelected) => node
  handleChange: func,
  selectProps: shape({
    items: arrayOf(any).isRequired,
    itemToString: func.isRequired,
    // anything else to go in to useSelect, like onSelectedItemChange or selectedItem
    // see https://github.com/downshift-js/downshift/tree/master/src/hooks/useSelect
  }).isRequired, // useSelect props
};

const MultiSelect = React.memo(props => {
  const {
    className,
    disabled,
    label,
    placeholder,
    selectProps,
    handleChange,
    ...otherProps
  } = props;

  const [context, setContext] = useContext(AppContext);

  const { items, itemToString, selectedItems: stateItems } = selectProps;

  const [selectedItems, setSelectedItems] = useState(stateItems);

  const stateReducer = (state, actionAndChanges) => {
    const { changes, type } = actionAndChanges;

    function keyDown(changes, state) {
      if (state.highlightedIndex < items.length - 1) {
        return {
          ...changes,
          isOpen: true,
          highlightedIndex: state.highlightedIndex + 1,
        };
      }
      else {
        return {
          ...changes,
        };
      }
    }

    function keyUp(changes, state) {
      if (state.highlightedIndex >= 1) {
        return {
          ...changes,
          isOpen: true,
          highlightedIndex: state.highlightedIndex - 1,
        };
      }
      else {
        return {
          ...changes,
        };
      }
    }

    switch (type) {
      case useCombobox.stateChangeTypes.MenuKeyDownArrowDown:
        return keyDown(changes, state);
      case useCombobox.stateChangeTypes.MenuKeyDownArrowUp:
        return keyUp(changes, state);
      case useCombobox.stateChangeTypes.MenuKeyDownEscape:
        setContext(prevState => ({ ...prevState, filterOpen: "" }));
        return { ...changes, isOpen: false };
      case useCombobox.stateChangeTypes.MenuKeyDownHome:
        return { ...changes, highlightedIndex: 0 };
      case useCombobox.stateChangeTypes.MenuKeyDownEnd:
        return {
          ...changes,
          highlightedIndex: items.length - 1,
        };
      case useCombobox.stateChangeTypes.MenuKeyDownEnter:
      case useCombobox.stateChangeTypes.MenuKeyDownSpaceButton:
      case useCombobox.stateChangeTypes.ItemClick:
        return {
          ...changes,
          isOpen: true, // keep menu open after selection.
          highlightedIndex: state.highlightedIndex,
          inputValue: "", // don't add the item string as input value at selection.
        };
      case useCombobox.stateChangeTypes.MenuBlur:
        if (isOpen) {
          setContext(prevState => ({ ...prevState, filterOpen: "" }));
        }
        break;
      case useCombobox.stateChangeTypes.FunctionCloseMenu:
        if (isOpen) {
          setContext(prevState => ({ ...prevState, filterOpen: "" }));
        }
        return {
          ...changes,
          isOpen: false, // keep menu open after selection.
          highlightedIndex: -1,
        };
      default:
        return changes;
    }
  };

  const sortedItems = R.sortBy(R.prop("label"), items);

  const selectState = useCombobox({
    items: sortedItems,
    stateReducer,
    selectedItem: null,
    onIsOpenChange: event => {
      const { isOpen } = event;
      setContext(prevState => ({
        ...prevState,
        filterOpen: isOpen ? "attributes" : "",
      }));
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) {
        return;
      }

      const index = selectedItems.indexOf(selectedItem);

      const newSelectedItems =
        index > -1
          ? selectedItems.filter(item => item.id !== selectedItem.id)
          : [...selectedItems, selectedItem];

      setSelectedItems(newSelectedItems);
      handleChange(newSelectedItems);
    },
  });

  const renderItem =
    props.renderItem ||
    (item => <React.Fragment>{itemToString(item)}</React.Fragment>);

  let {
    isOpen,
    getComboboxProps,
    getInputProps,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    closeMenu,
  } = selectState;

  // Use context instead of local state to prevent close on rerender
  isOpen = context.filterOpen === "attributes";

  const selectMenu = useRef();

  useClickAway({ isOpen, setOpen: () => closeMenu(), ref: selectMenu });

  let buttonText = placeholder;

  if (selectedItems.length > 2) {
    buttonText = `${selectedItems[0].label}, ${selectedItems[1].label} and others`;
  }
  else if (selectedItems.length > 1) {
    buttonText = `${selectedItems[0].label} and ${selectedItems[1].label}`;
  }
  else if (selectedItems.length === 1) {
    buttonText = `${selectedItems[0].label}`;
  }

  return (
    <div
      className={cx("select multiselect", className, {
        "select--open": isOpen,
      })}
      {...otherProps}
    >
      {label && (
        <label className="multiselect__label" {...getLabelProps()}>
          {label}
        </label>
      )}
      <div className="select__dropdown" {...getComboboxProps()}>
        <div className="select__button-holder">
          <input style={{ display: "none", }} {...getInputProps()} />
          <button
            type="button"
            className="select__button"
            {...getToggleButtonProps({
              disabled: disabled,
              onKeyDown: event => {
                if (event.key === "Escape") {
                  // Prevent Downshift's default "Escape" behavior because it
                  // sets the selectedItem to be null
                  event.nativeEvent.preventDownshiftDefault = true;

                  closeMenu();
                }
              },
            })}
          >
            <div className="select__button-text" title={buttonText}>
              {buttonText}
            </div>
          </button>
        </div>
        <ul className="select__menu" {...getMenuProps({ ref: selectMenu })}>
          {isOpen &&
            sortedItems.map((item, index) => {
              return (
                <li
                  className={cx("select__menu-item", {
                    "is-selected": R.find(
                      R.propEq("id", item.id),
                      selectedItems,
                    ),
                    "is-highlighted": highlightedIndex === index,
                  })}
                  key={index}
                  {...getItemProps({ item, index })}
                >
                  <span className="checkbox__custom" /> {renderItem(item)}
                </li>
              );
            })}
        </ul>
      </div>
    </div>
  );
});

MultiSelect.propTypes = propTypes;

export default MultiSelect;
